import { useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { Box, IconButton, InputAdornment } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SearchIcon from '@mui/icons-material/Search';

import { Table } from '@app/ui/table';
import { Tooltip } from '@app/ui/tooltip';
import { Label } from '@app/ui/label';
import { Text } from '@app/ui/forms';

import { SEVERITY_LABEL_COLOR, SEVERITY_VALUES, RULE_TYPE_VALUES, FAMILY_SLUGS } from '../constants';
import { RuleFamily, Rule } from '../types';
import { useRuleListTableState } from '../hooks/useRuleListTableState';
import { RuleDeleteDialog } from './RuleDeleteDialog';

export interface RuleListProps {
  readonly family: RuleFamily;
  readonly rules: Rule[];
  readonly loading?: boolean;
  readonly editable: boolean;
  readonly onDeleted: () => Promise<unknown> | void;
}

const SEARCH_WAIT_MS = 500;

const getSortValue = (rule: Rule, field: string): string | number => {
  switch (field) {
    case 'severity':
      return rule.severity;
    case 'type':
      return rule.type;
    case 'enabled':
      return rule.enabled ? 1 : 0;
    case 'section':
      return rule.section ?? '';
    case 'ruleText':
      return rule.ruleText;
    default:
      return rule.ruleId;
  }
};

export const RuleList = ({ family, rules, loading, editable, onDeleted }: RuleListProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const [rulePendingDelete, setRulePendingDelete] = useState<Rule | null>(null);

  const [tableState, onChangeTableState] = useRuleListTableState();

  // Local, not the shared `DebouncedText`: that one blanks its box on mount, so a search restored from the URL was hidden.
  const [searchInput, setSearchInput] = useState(tableState.search ?? '');
  const debouncedSearch = useDebouncedCallback(
    (search: string) => onChangeTableState({ search, page: 1 }),
    SEARCH_WAIT_MS,
  );

  const severityOptions = useMemo(
    () => [
      { id: 'all', name: t('rules.list.severityFilterAll') ?? '' },
      ...SEVERITY_VALUES.map(value => ({ id: value, name: t(`rules.severity.option.${value}`) ?? '' })),
    ],
    [t],
  );

  const typeOptions = useMemo(
    () => [
      { id: 'all', name: t('rules.list.typeFilterAll') ?? '' },
      ...RULE_TYPE_VALUES.map(value => ({ id: value, name: t(`rules.type.option.${value}`) ?? '' })),
    ],
    [t],
  );

  const filteredRules = useMemo(() => {
    let rows = rules;

    if (tableState.search) {
      const query = tableState.search.toLowerCase();

      rows = rows.filter(
        rule => rule.ruleId.toLowerCase().includes(query) || rule.ruleText.toLowerCase().includes(query),
      );
    }

    if (tableState.severity && tableState.severity !== 'all') {
      rows = rows.filter(rule => rule.severity === tableState.severity);
    }

    if (tableState.type && tableState.type !== 'all') {
      rows = rows.filter(rule => rule.type === tableState.type);
    }

    const [sort] = tableState.orderBy;

    if (sort) {
      const direction = sort.direction === 'desc' ? -1 : 1;

      rows = [...rows].sort((a, b) => {
        const left = getSortValue(a, sort.field);
        const right = getSortValue(b, sort.field);

        if (left === right) {
          return 0;
        }

        return left > right ? direction : -direction;
      });
    }

    return rows;
  }, [rules, tableState]);

  const pagedRules = useMemo(() => {
    const start = (tableState.page - 1) * tableState.limit;

    return filteredRules.slice(start, start + tableState.limit);
  }, [filteredRules, tableState.page, tableState.limit]);

  // `RuleEditor`'s back button navigates to a FIXED path and cannot know the list's page/sort/
  // filters, so they travel as router `state` for it to hand back unchanged. Called from the `columns` memo,
  // which therefore depends on `location.search` — without it the first-render (empty) query string is sent.
  const handleOpen = (rule: Rule) =>
    navigate(`/admin/settings/${FAMILY_SLUGS[family]}/rule/${rule.id}`, { state: { fromSearch: location.search } });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'ruleId',
        headerName: t('rules.list.columns.ruleId') ?? undefined,
        width: 130,
      },
      {
        field: 'ruleText',
        headerName: t('rules.list.columns.ruleText') ?? undefined,
        flex: 1,
        minWidth: 260,
        renderCell: (params: GridRenderCellParams<Rule>) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', overflow: 'hidden' }}>
            <span>{params.row.ruleText}</span>
          </Box>
        ),
      },
      {
        field: 'severity',
        headerName: t('rules.list.columns.severity') ?? undefined,
        width: 130,
        renderCell: (params: GridRenderCellParams<Rule>) => (
          <Label
            text={t(`rules.severity.option.${params.row.severity}`, params.row.severity)}
            color={SEVERITY_LABEL_COLOR[params.row.severity] ?? 'grey'}
            // 60px row line-height clips a Label at its natural height (AdminSettingsList precedent).
            sx={{ lineHeight: 'normal' }}
          />
        ),
      },
      {
        field: 'type',
        headerName: t('rules.list.columns.type') ?? undefined,
        width: 140,
        valueGetter: (_value, row: Rule) => t(`rules.type.option.${row.type}`, row.type),
      },
      {
        field: 'enabled',
        headerName: t('rules.list.columns.enabled') ?? undefined,
        width: 110,
        renderCell: (params: GridRenderCellParams<Rule>) => (
          <Label
            text={params.row.enabled ? t('general.option.yes') : t('general.option.no')}
            color={params.row.enabled ? 'success' : 'grey'}
            sx={{ lineHeight: 'normal' }}
          />
        ),
      },
      {
        field: 'actions',
        headerName: t('user.field.actions.label') ?? undefined,
        type: 'actions',
        // A `type: 'actions'` cell eats 36px/20px of padding, so a second icon needs more WIDTH.
        width: editable ? 130 : 80,
        align: 'center',
        getActions: (params: GridRowParams<Rule>) => {
          const actions = [
            <Tooltip
              key="open"
              title={(editable ? t('rules.list.editTooltip') : t('rules.list.viewTooltip')) ?? undefined}
              placement="top"
            >
              <IconButton
                className="filledTonal"
                color="secondary"
                sx={{ p: 0.5, width: 33, height: 33, borderRadius: '50%' }}
                onClick={() => handleOpen(params.row)}
              >
                {editable ? (
                  <EditOutlinedIcon sx={{ fontSize: 17 }} />
                ) : (
                  <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                )}
              </IconButton>
            </Tooltip>,
          ];

          if (editable) {
            actions.push(
              <Tooltip key="delete" title={t('rules.delete.button') ?? undefined} placement="top">
                <IconButton
                  className="filledTonal"
                  color="error"
                  sx={{ p: 0.5, width: 33, height: 33, borderRadius: '50%' }}
                  onClick={() => setRulePendingDelete(params.row)}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>,
            );
          }

          return actions;
        },
      },
    ],
    [t, editable, family, location.search],
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box sx={{ width: { xs: '100%', sm: 320 } }}>
          <Text
            label={t('application.field.search.label')}
            placeholder={t('rules.list.searchPlaceholder') ?? undefined}
            value={searchInput}
            onChange={e => {
              setSearchInput(e.target.value);
              debouncedSearch(e.target.value);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="medium" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 180 } }}>
          <Text
            select
            label={t('rules.list.columns.severity')}
            options={severityOptions}
            value={tableState.severity}
            onChange={e => onChangeTableState({ severity: e.target.value, page: 1 })}
          />
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 180 } }}>
          <Text
            select
            label={t('rules.list.columns.type')}
            options={typeOptions}
            value={tableState.type}
            onChange={e => onChangeTableState({ type: e.target.value, page: 1 })}
          />
        </Box>
      </Box>

      <Table
        loading={loading}
        columns={columns}
        rows={pagedRules}
        rowCount={filteredRules.length}
        state={tableState}
        onChangeState={onChangeTableState}
      />

      {rulePendingDelete && (
        <RuleDeleteDialog
          open
          onClose={() => setRulePendingDelete(null)}
          rule={rulePendingDelete}
          onDeleted={async () => {
            setRulePendingDelete(null);
            await onDeleted();
          }}
        />
      )}
    </Box>
  );
};
